const PERMISSION_COLOR_PALETTE = [
  '#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#d97706', '#c026d3',
  '#059669', '#4f46e5', '#e11d48', '#0284c7', '#65a30d', '#7c3aed', '#d946ef', '#0d9488',
];

/**
 * Deterministically maps a permission key to one of 16 distinct, high-contrast colors,
 * so the same permission key always renders with the same color across the app.
 *
 * @param key - The permission key to hash (e.g. 'Transaction.Read').
 * @returns A hex color string from the fixed palette.
 */
export const getPermissionColor = (key: string): string => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  return PERMISSION_COLOR_PALETTE[Math.abs(hash) % PERMISSION_COLOR_PALETTE.length];
};

/**
 * Reads the dev/admin "highlight permissions" toggle from `localStorage`.
 * Used to enable the `PermissionHighlightOverlay` visual auditing mode.
 *
 * @returns `true` if the `prism_highlight_permissions` localStorage flag is set to `'true'`.
 */
export const getHighlightPermissions = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('prism_highlight_permissions') === 'true';
};

/** Atomic capabilities — used by can*() helpers for in-component UI gating.
 *  These helpers accept `userPermissionKeys` (flat string[]) from useCurrentUser().
 */
export const Permissions = {
  transaction: {
    read: 'Transaction.Read',
    create: 'Transaction.Create',
    update: 'Transaction.Update',
    cancel: 'Transaction.Cancel',
    submit: 'Transaction.Submit',
    markMapped: 'Transaction.MarkMapped',
    qualifyOrDeclineOps: 'Transaction.QualifyOrDeclineOperations',
    qualifyOrDeclineCeo: 'Transaction.QualifyOrDeclineCeo',
    changeHealth: 'Transaction.ChangeHealth',
    toggleEditLock: 'Transaction.ToggleEditLock',
    jarvis: 'Transaction.Jarvis',
    updateCOC: 'Transaction.UpdateCOC',
    updateIRR: 'Transaction.UpdateIRR',
    repairData: 'Transaction.RepairData',
    approveInvestmentAnalyst: 'TransactionTeam.ApproveInvestmentAnalyst',
    approveFinanceLead: 'TransactionTeam.ApproveFinanceLead',
    approveTransactionLead: 'TransactionTeam.ApproveTransactionLead',
    updateTransactionLead: 'TransactionTeam.UpdateTransactionTeamLead',
  },
  transactionPriceType: {
    write: 'TransactionPriceType.Write',
  },
  marketValue: {
    read: 'MarketValue.Read',
  },
  admin: {
    manage: 'Admin.Manage',
  },
} as const;

/**
 * Checks whether the user holds the `Transaction.Read` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can read transactions.
 */
export const canReadTransactions = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.read);
}

/**
 * Checks whether the user holds the `Transaction.MarkMapped` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can mark an opportunity as mapped.
 */
export const canMarkOpportunityMapped = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.markMapped);
}

/**
 * Checks whether the user holds the `Transaction.Create` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can create an opportunity.
 */
export const canCreateOpportunity = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.create);
}

/**
 * Checks whether the user holds the `Transaction.Update` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can update a transaction.
 */
export const canUpdateTransaction = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.update);
}

/**
 * Checks whether the user holds the `Transaction.RepairData` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can repair transaction data (e.g. phase/milestone sync).
 */
export const canRepairTransactionData = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.repairData);
}

/**
 * Checks whether the user holds the `Transaction.Cancel` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can cancel an opportunity.
 */
export const canCancelOpportunity = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.cancel);
}

/**
 * Checks whether the user holds the `Transaction.ChangeHealth` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can change a transaction's health.
 */
export const canChangeTransactionHealth = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.changeHealth);
}

/**
 * Checks whether the user holds the `Transaction.Submit` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can submit an opportunity.
 */
export const canSubmitOpportunity = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.submit);
}

/**
 * Checks whether the user holds the `Transaction.QualifyOrDeclineOperations` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can qualify/decline the opportunity on behalf of Operations.
 */
export const canQualifyOpsOpportunity = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.qualifyOrDeclineOps);
}

/**
 * Checks whether the user holds the `Transaction.QualifyOrDeclineCeo` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can qualify/decline the opportunity on behalf of the CEO.
 */
export const canQualifyCeoOpportunity = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.qualifyOrDeclineCeo);
}

/**
 * Checks whether the user holds the `Transaction.Jarvis` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can update Jarvis-related transaction fields.
 */
export const canUpdateTransactionJarvis = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.jarvis);
}

/**
 * Checks whether the user holds the `TransactionTeam.ApproveInvestmentAnalyst` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can approve as investment analyst.
 */
export const canApproveInvestmentAnalyst = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.approveInvestmentAnalyst);
}

/**
 * Checks whether the user holds the `TransactionTeam.ApproveFinanceLead` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can approve as finance lead.
 */
export const canApproveFinanceLead = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.approveFinanceLead);
}

/**
 * Checks whether the user holds the `TransactionTeam.ApproveTransactionLead` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can approve as transaction lead.
 */
export const canApproveTransactionLead = (permissions: string[] | undefined) => {
    return permissions?.includes(Permissions.transaction.approveTransactionLead);
}

/**
 * Checks whether the user holds the `TransactionTeam.UpdateTransactionTeamLead` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can update the transaction team lead.
 */
export const canUpdateTransactionTeamLead = (permissions: string[] | undefined) => {
  return permissions?.includes(Permissions.transaction.updateTransactionLead);
}

/**
 * Checks whether the user holds the `Transaction.ToggleEditLock` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can toggle the transaction edit lock.
 */
export const canToggleEditLock = (permissions: string[] | undefined) => {
  return permissions?.includes(Permissions.transaction.toggleEditLock);
}

/**
 * Checks whether the user holds any permission that grants an approver role — Ops/CEO qualify-or-decline, or investment analyst/finance lead/transaction lead approval.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user holds any of the approval-related permissions.
 */
export const hasApproverRole = (permissions: string[] | undefined) => {
  return permissions?.includes(Permissions.transaction.qualifyOrDeclineOps)
    || permissions?.includes(Permissions.transaction.qualifyOrDeclineCeo)
    || permissions?.includes(Permissions.transaction.approveInvestmentAnalyst)
    || permissions?.includes(Permissions.transaction.approveFinanceLead)
    || permissions?.includes(Permissions.transaction.approveTransactionLead);
}

/**
 * Checks whether the user holds the `MarketValue.Read` permission.
 *
 * @param permissions - The current user's flat permission key list from `useCurrentUser()`.
 * @returns `true` if the user can read market value data.
 */
export const canReadMarketValue = (permissions: string[] | undefined) => {
  return permissions?.includes(Permissions.marketValue.read) ?? false;
}

/**
 * PRM-504 task completion gate. Returns the required permission keys that the
 * user is MISSING (empty array => the user may complete the task). The user
 * must hold ALL of a task's `requiredPermissionKeys` to complete it.
 *
 * @param requiredPermissionKeys - Keys required by the task definition.
 * @param userPermissionKeys - The current user's flat permission key list.
 * @returns The subset of required keys the user does not hold.
 */
export const missingTaskPermissions = (
  requiredPermissionKeys: string[] | undefined | null,
  userPermissionKeys: string[] | undefined
): string[] => {
  if (!requiredPermissionKeys || requiredPermissionKeys.length === 0) return [];
  const held = new Set(userPermissionKeys ?? []);
  return requiredPermissionKeys.filter((k) => !held.has(k));
};

/**
 * Returns true if allowedRoles is empty/undefined (no restriction)
 * or the user has at least one of the listed roles.
 */
export const hasRequiredRoles = (
  allowedRoles: string[] | undefined | null,
  userRoles: string[] | undefined
): boolean => {
  if (allowedRoles === undefined || (Array.isArray(allowedRoles) && allowedRoles.length === 0)) return true;
  if (!allowedRoles) return false;
  if (!userRoles || userRoles.length === 0) return false;
  return allowedRoles.some((role) => userRoles.includes(role));
};

/**
 * Returns true if allowedPermissions is empty/undefined (no restriction)
 * or the user has at least one of the listed permission keys.
 * Optionally pass appKey to scope the check to a specific app.
 */
export const hasRequiredPermissions = (
  allowedPermissions: string[] | undefined | null,
  userPermissions: import('@repo/services').UserPermissionVm[] | string[] | undefined,
  appKey?: string
): boolean => {
  if (allowedPermissions === undefined || (Array.isArray(allowedPermissions) && allowedPermissions.length === 0)) return true;
  if (!allowedPermissions) return false;
  if (!userPermissions || userPermissions.length === 0) return false;

  // Normalise to string[] — support both old string[] and new UserPermissionVm[]
  const keys: string[] = (userPermissions as any[]).map(p =>
    typeof p === 'string' ? p : (appKey ? (p.appKey === appKey ? p.permissionKey : null) : p.permissionKey)
  ).filter(Boolean) as string[];

  return allowedPermissions.some(perm => keys.includes(perm));
};
